"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
// tslint:disable:react-hooks-nesting
var redux_1 = require("redux");
var react_dom_1 = __importDefault(require("react-dom"));
var react_1 = __importStar(require("react"));
var shallow_equal_1 = require("./shallow-equal");
/** id sequence */
var SEQ = 0;
// Custom "null" because mapState can return the js null we must be able to
// differentiate from it
var nil = {}; // cross browser Symbol hack :)
var NoProviderError = /** @class */ (function (_super) {
    __extends(NoProviderError, _super);
    function NoProviderError() {
        return _super.call(this, "<HooksProvider> wrapping missing for useRedux*()?") || this;
    }
    return NoProviderError;
}(Error));
/**
 * Create ponyfill Map if native is not available
 */
function createMap() {
    if (typeof Map !== "undefined") {
        return new Map();
    }
    var poorMap = {};
    // ponyfill for Map
    return {
        set: function (id, updater) {
            poorMap[id] = updater;
        },
        delete: function (id) {
            delete poorMap[id];
        },
        forEach: function (cb) {
            for (var id in poorMap) {
                cb(poorMap[id]);
            }
        },
    };
}
var StoreContext = react_1.default.createContext({
    updaters: createMap(),
});
function HooksProvider(props) {
    // Mutable updaters list of all useReduxState() users
    var updaters = react_1.useRef(createMap());
    /**
     * Notify subscribers
     */
    var notify = function () {
        react_dom_1.default.unstable_batchedUpdates(function () {
            updaters.current.forEach(function (updater) { return updater(); });
        });
    };
    var preEffectState = react_1.useMemo(function () { return props.store.getState(); }, [props.store]);
    react_1.useEffect(function () {
        // Children might dispatch during effect exection. Check for that and
        // notify them
        if (preEffectState !== props.store.getState()) {
            notify();
        }
        // Setup only one listener for the provider so we can batch update all
        // hook users without causing tearing
        return props.store.subscribe(notify);
    }, [props.store]);
    // Context values never update. We put the store directly and the updates
    // list into it
    return (react_1.default.createElement(StoreContext.Provider, { value: { store: props.store, updaters: updaters.current } }, props.children));
}
exports.HooksProvider = HooksProvider;
/**
 * Use Redux dispatch
 */
function useDispatch() {
    var store = react_1.useContext(StoreContext).store;
    if (!store) {
        throw new NoProviderError();
    }
    return store.dispatch;
}
exports.useDispatch = useDispatch;
function createUseSelect() {
    function useSelect(select, produce, deps) {
        var ref = react_1.useRef({
            result: nil,
            prevSelection: nil,
        });
        function defaultProduce(state) {
            return state;
        }
        return exports.useMapState(function (state) {
            var selection = select(state);
            var selectionChanged = !shallow_equal_1.shallowEqual(selection, ref.current.prevSelection);
            if (selectionChanged) {
                ref.current.prevSelection = selection;
            }
            if (!selectionChanged && ref.current.result !== nil) {
                return ref.current.result;
            }
            var res = produce
                ? produce(selection)
                : defaultProduce(selection);
            ref.current.result = res;
            return res;
        }, deps);
    }
    return useSelect;
}
exports.createUseSelect = createUseSelect;
/**
 * Bound actions creators object to Redux dispatch. Memoized.
 */
function useActionCreators(actionCreators) {
    var dispatch = useDispatch();
    return react_1.useMemo(function () { return redux_1.bindActionCreators(actionCreators, dispatch); }, [
        actionCreators,
    ]);
}
exports.useActionCreators = useActionCreators;
function useForceRender() {
    var _a = react_1.useState(0), _ = _a[0], setUpdateCount = _a[1];
    return function trigger() {
        setUpdateCount(function (count) { return count + 1; });
    };
}
function useDidDepsChange(deps) {
    if (!deps) {
        return false;
    }
    var memoDeps = react_1.useMemo(function () { return deps; }, deps);
    // changed when useMemo returns the same deps reference
    return memoDeps == deps;
}
function createUseMapState() {
    return function useMapState(mapState, deps) {
        var _a = react_1.useContext(StoreContext), store = _a.store, updaters = _a.updaters;
        if (!store) {
            throw new NoProviderError();
        }
        /**
         * Hold inital store state when the component mounts
         */
        var initialState = react_1.useMemo(function () { return store.getState(); }, [store]);
        /**
         * Reference to the previously mapped state
         */
        var prevRef = react_1.useRef(nil);
        /**
         * When this is set can bailout state mapping and just return this
         */
        var bailoutRef = react_1.useRef(nil);
        /**
         * Trigger render from store updates
         */
        var triggerRender = useForceRender();
        /**
         * Detect deps change when using the deps array
         */
        var depsChanged = useDidDepsChange(deps);
        /**
         * Reference to the mapState function
         */
        var mapStateRef = react_1.useRef();
        mapStateRef.current = mapState;
        /**
         * Get mapped value from the state
         */
        var getMappedValue = function () {
            var state = store.getState();
            if (!mapStateRef.current) {
                return state;
            }
            var res = mapStateRef.current(state);
            if (process.env.NODE_ENV !== "production") {
                var check = mapStateRef.current(state);
                if (!shallow_equal_1.is(check, res)) {
                    console.warn("useMapState returns new identity on every run. This causes the component to render on every Redux state change. Consider use using useSelect().", res);
                }
            }
            return res;
        };
        // Set initial mapped states for the first render
        if (prevRef.current === nil) {
            prevRef.current = bailoutRef.current = getMappedValue();
        }
        react_1.useEffect(function () {
            // handle updates from the store
            var update = function () {
                var next = getMappedValue();
                if (!shallow_equal_1.is(prevRef.current, next)) {
                    bailoutRef.current = next;
                    prevRef.current = next;
                    triggerRender();
                }
            };
            // Mutate the updaters map so the subscription in the provider can
            // update this hook
            var id = ++SEQ;
            updaters.set(id, update);
            // Self update if the store did update between first render and this effect
            if (initialState !== store.getState()) {
                update();
            }
            return function () {
                // Remove the updater on unmount or store change
                updaters.delete(id);
                // clear cached on store change
                prevRef.current = nil;
                bailoutRef.current = nil;
            };
        }, [store]);
        // Bailout with the previously mapped state if we have deps and they did not
        // change
        if (deps && !depsChanged && bailoutRef.current === nil) {
            bailoutRef.current = prevRef.current;
        }
        // Use the bailout if we have one
        if (bailoutRef.current !== nil) {
            var ret = bailoutRef.current;
            bailoutRef.current = nil;
            return ret;
        }
        // Normal render. Must map the state because the mapping function might
        // have changed
        return (prevRef.current = getMappedValue());
    };
}
exports.createUseMapState = createUseMapState;
function createUsePassiveMapState() {
    return function usePassiveMapState(mapState, deps) {
        var store = react_1.useContext(StoreContext).store;
        if (!store) {
            throw new NoProviderError();
        }
        if (deps) {
            return react_1.useMemo(function () {
                return mapState(store.getState());
            }, deps);
        }
        return mapState(store.getState());
    };
}
exports.createUsePassiveMapState = createUsePassiveMapState;
exports.useMapState = createUseMapState();
exports.usePassiveMapState = createUsePassiveMapState();
exports.useSelect = createUseSelect();
/**
 * Create custom typed versions of the hooks
 */
function createHooks() {
    return {
        useMapState: createUseMapState(),
        usePassiveMapState: createUsePassiveMapState(),
        useSelect: createUseSelect(),
        useDispatch: useDispatch,
        useActionCreators: useActionCreators,
    };
}
exports.createHooks = createHooks;
//# sourceMappingURL=redux-hooks.js.map